import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// Libs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNodeUpdate } from '~/hooks/useNodeUpdate';
import { useNodeId } from '@xyflow/react';

// Utilitários
import { NodeHeaderCustomDeleteAction } from '~/utils/NodeHeaderCustomDeleteAction';
import { NodeHeaderCustomAlertAction } from '~/utils/NodeHeaderCustomAlertAction';
import { InputRichVariavel } from '~/utils/InputRichVariavel';
import { CustomHandle } from '~/utils/CustomHandle';
import { CustomAvatarSelect } from '~/utils/CustomAvatarSelect';

// Node de interação do avatar
export const InteragirAvatarNode = ({ data }) => {
    const id = useNodeId();
    const { onNodesChange } = useNodeUpdate();
    const avatars = useSelector((state) => state.avatars);
    
    // Estados principais
    const [selectedAvatar, setSelectedAvatar] = useState(data.selectedAvatar || "");
    const [tipoBalao, setTipoBalao] = useState(data.tipoBalao || "dizer");
    const [posicaoBalao, setPosicaoBalao] = useState(data.posicaoBalao || "acima");
    const [mensagem, setMensagem] = useState(data.mensagem || "");
    const [isExpanded, setIsExpanded] = useState(false);
    const [audioAtivo, setAudioAtivo] = useState(data.audioAtivo || false);
    const [armazenarResposta, setArmazenarResposta] = useState(data.armazenarResposta || "nao");
    const [tipoResposta, setTipoResposta] = useState(data.tipoResposta || "texto");
    const [nomeVariavel, setNomeVariavel] = useState(data.nomeVariavel || "");
    const [modoExibicao, setModoExibicao] = useState(data.modoExibicao || "confirmar");
    const [tempoExibicao, setTempoExibicao] = useState(data.tempoExibicao || 3);

    // Sincroniza com data para undo/redo
    useEffect(() => { 
        setSelectedAvatar(data.selectedAvatar || ""); 
    }, [data.selectedAvatar]);
    useEffect(() => { setTipoBalao(data.tipoBalao || "dizer"); }, [data.tipoBalao]);
    useEffect(() => { setPosicaoBalao(data.posicaoBalao || "acima"); }, [data.posicaoBalao]);
    useEffect(() => { setMensagem(data.mensagem || ""); }, [data.mensagem]);
    useEffect(() => { setAudioAtivo(data.audioAtivo || false); }, [data.audioAtivo]);
    useEffect(() => { setArmazenarResposta(data.armazenarResposta || "nao"); }, [data.armazenarResposta]);
    useEffect(() => { setTipoResposta(data.tipoResposta || "texto"); }, [data.tipoResposta]);
    useEffect(() => { setNomeVariavel(data.nomeVariavel || ""); }, [data.nomeVariavel]);
    useEffect(() => { setModoExibicao(data.modoExibicao || "confirmar"); }, [data.modoExibicao]);
    useEffect(() => { setTempoExibicao(data.tempoExibicao || 3); }, [data.tempoExibicao]);

    // Validações
    const [listaPendencias, setListaPendencias] = useState([]);
    const conferePendencias = () => {
        const pendencias = [];
        if (!selectedAvatar) pendencias.push("Avatar não selecionado");
        if (!mensagem.trim()) pendencias.push("Mensagem não preenchida");
        if (armazenarResposta === "sim" && !nomeVariavel.trim()) {
            pendencias.push("Nome da variável não preenchido");
        }
        setListaPendencias(pendencias);
        return pendencias;
    };

    // Handlers de atualização
    const updateNodeData = (newData) => {
        onNodesChange([{ id, type: 'update', data: { ...data, ...newData } }]);
    };

    const handleAvatarChange = (avatarIndex) => {
        setSelectedAvatar(avatarIndex);
        const newData = { ...data, selectedAvatar: avatarIndex };
        onNodesChange([{ id, type: 'update', data: newData }]);
    };

    const handleTipoBalaoChange = (tipo) => {
        setTipoBalao(tipo);
        updateNodeData({ tipoBalao: tipo });
    };

    const handlePosicaoBalaoChange = (posicao) => {
        setPosicaoBalao(posicao);
        updateNodeData({ posicaoBalao: posicao });
    };

    const handleMensagemChange = (msg) => {
        setMensagem(msg);
        updateNodeData({ mensagem: msg });
    };

    const handleAudioChange = () => {
        const novoAudio = !audioAtivo;
        setAudioAtivo(novoAudio);
        updateNodeData({ audioAtivo: novoAudio });
    };

    const handleArmazenarRespostaChange = (valor) => {
        setArmazenarResposta(valor);
        updateNodeData({ armazenarResposta: valor });
    };

    const handleTipoRespostaChange = (tipo) => {
        setTipoResposta(tipo);
        updateNodeData({ tipoResposta: tipo });
    };

    const handleNomeVariavelChange = (nome) => {
        setNomeVariavel(nome);
        updateNodeData({ nomeVariavel: nome });
    };

    const handleModoExibicaoChange = (modo) => {
        setModoExibicao(modo);
        updateNodeData({ modoExibicao: modo });
    };

    const handleTempoExibicaoChange = (e) => {
        const tempo = e.target.value;
        setTempoExibicao(tempo);
        updateNodeData({ tempoExibicao: tempo });
    };

    useEffect(() => { conferePendencias(); }, [selectedAvatar, mensagem, armazenarResposta, nomeVariavel]);

    // Opções de avatar para o select
    const avatarOptions = avatars
        .map((avatar, index) => ({ // Remove o filter primeiro
            originalIndex: index,
            avatar: avatar
        }))
        .filter(item => item.avatar.nome) // Agora filtra mantendo o índice original
        .map(item => ({
            value: item.originalIndex.toString(),
            label: item.avatar.nome
        }));

    return (
        <div className="node node-interagir-avatar">
            <div className="titulo-node">
                <FontAwesomeIcon icon="fa-solid fa-comment-dots" />
                {data.label.toUpperCase()}
            </div>
            <div className="linha-separadora" />
            <div className="node-geral">
                <div className="node-botoes">
                    <button title="Executar este comando" onClick={() => alert("Executar")}>
                        <FontAwesomeIcon icon="fa-solid fa-circle-play" />
                    </button>
                    <NodeHeaderCustomDeleteAction />
                    {listaPendencias.length > 0 && <NodeHeaderCustomAlertAction conferePendencias={conferePendencias} nodeData={data} />}
                </div>
                <div className="node-principal">
                    <div className="body-node"></div>
                    
                    {/* Seleção de Avatar */}
                    <div style={{ marginBottom: '12px' }}>
                        <span className="icon">Avatar:</span>
                        
                        {selectedAvatar && avatars[parseInt(selectedAvatar)] ? (
                            // Preview do avatar selecionado
                            <div style={{
                                marginTop: '8px',
                                padding: '12px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '12px',
                                backgroundColor: '#f9f9f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#4CAF50';
                                e.currentTarget.style.backgroundColor = '#f1f8e9';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(76, 175, 80, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.backgroundColor = '#f9f9f9';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                            }}
                            >
                                {/* Avatar circular */}
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: '#4CAF50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    {avatars[parseInt(selectedAvatar)].imagem ? (
                                        <img 
                                            src={avatars[parseInt(selectedAvatar)].imagem} 
                                            alt={avatars[parseInt(selectedAvatar)].nome}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <FontAwesomeIcon icon="fa-solid fa-user" />
                                    )}
                                </div>
                                
                                {/* Nome do avatar */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#333',
                                        marginBottom: '2px'
                                    }}>
                                        {avatars[parseInt(selectedAvatar)].nome}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#666'
                                    }}>
                                        Avatar selecionado
                                    </div>
                                </div>
                                
                                {/* Botão remover */}
                                <button
                                    onClick={() => handleAvatarChange('')}
                                    style={{
                                        padding: '8px 16px',
                                        fontSize: '12px',
                                        backgroundColor: '#ff6b6b',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontWeight: '500',
                                        boxShadow: '0 2px 4px rgba(255, 107, 107, 0.3)',
                                        transition: 'all 0.2s ease',
                                        minWidth: '80px',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ff5252';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 82, 82, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ff6b6b';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 107, 107, 0.3)';
                                    }}
                                    title="Remover avatar e selecionar outro"
                                >
                                    <FontAwesomeIcon icon="fa-solid fa-exchange-alt" />
                                    Trocar
                                </button>
                            </div>
                        ) : (
                            // Seletor quando nenhum avatar está selecionado
                            <div style={{ marginTop: '8px' }}>
                                <CustomAvatarSelect
                                    key={`avatar-select-${id}`}
                                    value={selectedAvatar}
                                    onChange={handleAvatarChange}
                                    options={avatarOptions}
                                    placeholder="Arraste um avatar aqui ou selecione abaixo"
                                />
                                {avatarOptions.length === 0 && (
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '8px',
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffeaa7',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#856404'
                                    }}>
                                        ⚠️ Nenhum avatar disponível. Crie um avatar no painel lateral primeiro.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Tipo de Balão */}
                    <div style={{ marginBottom: '12px' }}>
                        <span className="icon">Tipo de balão:</span>
                        <div style={{ marginTop: '5px' }}>
                            <label style={{ marginRight: '15px' }}>
                                <input 
                                    type="radio" 
                                    value="dizer" 
                                    checked={tipoBalao === 'dizer'} 
                                    onChange={() => handleTipoBalaoChange('dizer')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-comment" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                Dizer
                            </label>
                            <label>
                                <input 
                                    type="radio" 
                                    value="pensar" 
                                    checked={tipoBalao === 'pensar'} 
                                    onChange={() => handleTipoBalaoChange('pensar')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-cloud" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                Pensar
                            </label>
                        </div>
                    </div>

                    {/* Posição do Balão */}
                    <div style={{ marginBottom: '12px' }}>
                        <span className="icon">Posição do balão:</span>
                        <div style={{ marginTop: '5px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', fontSize: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="acima-esquerda" 
                                    checked={posicaoBalao === 'acima-esquerda'} 
                                    onChange={() => handlePosicaoBalaoChange('acima-esquerda')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-up" style={{ marginLeft: '3px', marginRight: '2px' }} />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-left" style={{ marginRight: '3px' }} />
                                ↖
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="acima" 
                                    checked={posicaoBalao === 'acima'} 
                                    onChange={() => handlePosicaoBalaoChange('acima')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-up" style={{ marginLeft: '3px', marginRight: '3px' }} />
                                Acima
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="acima-direita" 
                                    checked={posicaoBalao === 'acima-direita'} 
                                    onChange={() => handlePosicaoBalaoChange('acima-direita')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-up" style={{ marginLeft: '3px', marginRight: '2px' }} />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-right" style={{ marginRight: '3px' }} />
                                ↗
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="esquerda" 
                                    checked={posicaoBalao === 'esquerda'} 
                                    onChange={() => handlePosicaoBalaoChange('esquerda')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-left" style={{ marginLeft: '3px', marginRight: '3px' }} />
                                Esquerda
                            </label>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: '#f0f0f0',
                                border: '2px solid #ddd',
                                borderRadius: '8px',
                                padding: '6px',
                                color: '#666'
                            }}>
                                <FontAwesomeIcon icon="fa-solid fa-user" style={{ fontSize: '16px' }} />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="direita" 
                                    checked={posicaoBalao === 'direita'} 
                                    onChange={() => handlePosicaoBalaoChange('direita')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-right" style={{ marginLeft: '3px', marginRight: '3px' }} />
                                Direita
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="abaixo-esquerda" 
                                    checked={posicaoBalao === 'abaixo-esquerda'} 
                                    onChange={() => handlePosicaoBalaoChange('abaixo-esquerda')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-down" style={{ marginLeft: '3px', marginRight: '2px' }} />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-left" style={{ marginRight: '3px' }} />
                                ↙
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="abaixo" 
                                    checked={posicaoBalao === 'abaixo'} 
                                    onChange={() => handlePosicaoBalaoChange('abaixo')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-down" style={{ marginLeft: '3px', marginRight: '3px' }} />
                                Abaixo
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    type="radio" 
                                    value="abaixo-direita" 
                                    checked={posicaoBalao === 'abaixo-direita'} 
                                    onChange={() => handlePosicaoBalaoChange('abaixo-direita')} 
                                />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-down" style={{ marginLeft: '3px', marginRight: '2px' }} />
                                <FontAwesomeIcon icon="fa-solid fa-arrow-right" style={{ marginRight: '3px' }} />
                                ↘
                            </label>
                        </div>
                    </div>

                    {/* Mensagem */}
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                            <span className="icon">Mensagem:</span>
                            <button 
                                onClick={handleAudioChange}
                                style={{ 
                                    marginLeft: '10px',
                                    padding: '4px 8px',
                                    border: audioAtivo ? '2px solid #4CAF50' : '1px solid #ccc',
                                    borderRadius: '4px',
                                    background: audioAtivo ? '#E8F5E8' : '#f9f9f9',
                                    cursor: 'pointer'
                                }}
                                title={audioAtivo ? "Áudio ativado" : "Áudio desativado"}
                            >
                                <FontAwesomeIcon 
                                    icon={audioAtivo ? "fa-solid fa-volume-up" : "fa-solid fa-volume-mute"} 
                                    style={{ color: audioAtivo ? '#4CAF50' : '#999' }}
                                />
                            </button>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                            <button 
                                onClick={() => setIsExpanded(!isExpanded)} 
                                style={{ 
                                    marginRight: "8px", 
                                    padding: "8px", 
                                    cursor: "pointer",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    background: "#fafafa",
                                    minWidth: "32px",
                                    minHeight: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                                title={isExpanded ? "Minimizar editor" : "Expandir para textarea"}
                            >
                                {isExpanded ? <FontAwesomeIcon icon="fa-solid fa-minus" /> : <FontAwesomeIcon icon="fa-solid fa-plus" />}
                            </button>
                            <div style={{ flex: 1, maxWidth: '300px', minWidth: '250px', width: '100%' }}>
                                <InputRichVariavel
                                    value={mensagem}
                                    onChange={handleMensagemChange}
                                    placeholder="Mensagem que o avatar vai falar/pensar ou arraste uma variável"
                                    multiline={isExpanded}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Armazenar Resposta */}
                    <div style={{ marginBottom: '12px' }}>
                        <span className="icon">Armazenar resposta:</span>
                        <div style={{ marginTop: '5px' }}>
                            <label style={{ marginRight: '15px' }}>
                                <input 
                                    type="radio" 
                                    value="nao" 
                                    checked={armazenarResposta === 'nao'} 
                                    onChange={() => handleArmazenarRespostaChange('nao')} 
                                />
                                Não
                            </label>
                            <label>
                                <input 
                                    type="radio" 
                                    value="sim" 
                                    checked={armazenarResposta === 'sim'} 
                                    onChange={() => handleArmazenarRespostaChange('sim')} 
                                />
                                Sim
                            </label>
                        </div>
                    </div>

                    {/* Opções quando armazenar resposta = SIM */}
                    {armazenarResposta === 'sim' && (
                        <div style={{ marginBottom: '12px', marginLeft: '20px', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '4px', background: '#f9f9f9' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <span className="icon">Tipo de resposta:</span>
                                <div style={{ marginTop: '5px' }}>
                                    <label style={{ marginRight: '15px' }}>
                                        <input 
                                            type="radio" 
                                            value="texto" 
                                            checked={tipoResposta === 'texto'} 
                                            onChange={() => handleTipoRespostaChange('texto')} 
                                        />
                                        <FontAwesomeIcon icon="fa-solid fa-keyboard" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                        Digitação livre (texto/número)
                                    </label>
                                </div>
                                <div style={{ marginTop: '5px' }}>
                                    <label>
                                        <input 
                                            type="radio" 
                                            value="logico" 
                                            checked={tipoResposta === 'logico'} 
                                            onChange={() => handleTipoRespostaChange('logico')} 
                                        />
                                        <FontAwesomeIcon icon="fa-solid fa-toggle-on" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                        Lógico (sim [verd.] / não [falso])
                                    </label>
                                </div>
                            </div>
                            <div>
                                <span className="icon">Armazenar na variável:</span>
                                <input 
                                    type="text" 
                                    value={nomeVariavel}
                                    onChange={(e) => handleNomeVariavelChange(e.target.value)}
                                    placeholder="Nome da variável"
                                    style={{ 
                                        width: '100%', 
                                        marginTop: '5px',
                                        padding: '6px 10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Opções quando armazenar resposta = NÃO */}
                    {armazenarResposta === 'nao' && (
                        <div style={{ marginBottom: '12px' }}>
                            <span className="icon">Opções de exibição:</span>
                            <div style={{ marginTop: '5px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>
                                    <input 
                                        type="radio" 
                                        value="confirmar" 
                                        checked={modoExibicao === 'confirmar'} 
                                        onChange={() => handleModoExibicaoChange('confirmar')} 
                                    />
                                    <FontAwesomeIcon icon="fa-solid fa-hand-pointer" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                    Aguardar confirmação
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center' }}>
                                    <input 
                                        type="radio" 
                                        value="tempo" 
                                        checked={modoExibicao === 'tempo'} 
                                        onChange={() => handleModoExibicaoChange('tempo')} 
                                    />
                                    <FontAwesomeIcon icon="fa-solid fa-clock" style={{ marginLeft: '5px', marginRight: '5px' }} />
                                    Mostrar mensagem por
                                    <input 
                                        type="number" 
                                        value={tempoExibicao}
                                        onChange={handleTempoExibicaoChange}
                                        min="1"
                                        max="60"
                                        style={{ 
                                            width: '50px', 
                                            margin: '0 5px',
                                            padding: '2px 5px',
                                            border: '1px solid #ccc',
                                            borderRadius: '3px'
                                        }}
                                    />
                                    segundos
                                </label>
                            </div>
                        </div>
                    )}

                    <CustomHandle type="target" position="top" connectionCount={1} />
                    <CustomHandle type="source" position="bottom" connectionCount={1} />
                </div>
            </div>
        </div>
    );
};
